/**
 * ───────────────────────────────────────────────────────────────────────────
 * GIGGONE LOGO SYSTEM — 5 CONCEPTS, ONE SOURCE OF TRUTH
 * ───────────────────────────────────────────────────────────────────────────
 *
 * The previous mark was a hard hat in a rounded square: instantly readable as
 * "construction company", but generic, unownable and impossible to tell apart
 * from a thousand local contractor logos. A marketplace brand needs a mark that
 * behaves like Uber / Stripe / Upwork / Airbnb: an abstract, geometric symbol
 * that becomes recognisable WITHOUT the wordmark.
 *
 * DESIGN CONSTRAINTS APPLIED TO ALL FIVE CONCEPTS
 *  - Drawn on a single 64×64 grid so every mark shares optical weight.
 *  - Maximum 3 drawn elements. Detail is what kills a favicon at 16px.
 *  - Stroke weight 6–7/64 (≈1.7px at 16px) — survives downscaling.
 *  - Exactly two inks: brand orange + a neutral. No gradients, no bevels, no
 *    photographic detail, nothing that needs anti-aliasing to read.
 *  - No houses, no hammers, no hard hats, no clipart.
 *  - Ships in two variants so one file covers every placement:
 *      tile  → self-contained navy squircle (app icon, favicon, social avatar;
 *              legible on ANY background because it brings its own)
 *      glyph → transparent, neutral ink = `currentColor` (inline in headers,
 *              email, print, dark mode — inherits the surrounding text colour)
 *
 * WHY THE ARTWORK IS A STRING AND NOT JSX
 * The same geometry has to render three ways: as React in the app, as a
 * `data:image/svg+xml` favicon in index.html, and as a downloadable .svg asset
 * for print / GoHighLevel / Mailgun templates. Authoring it as JSX would force
 * a second, hand-copied definition for the string uses — which is exactly how
 * a logo drifts out of sync across surfaces. Defining it once as markup means
 * the favicon can never disagree with the header.
 */

/** Brand inks. Kept local and literal so exported SVG assets are self-contained. */
export const LOGO_INK = {
  navy: '#0f172a',
  orange: '#f97316',
  white: '#ffffff',
} as const;

export type LogoVariant = 'tile' | 'glyph';

interface Inks {
  /** Brand accent. Always orange. */
  primary: string;
  /** Neutral ink. White on the navy tile, `currentColor` for inline glyphs. */
  neutral: string;
}

export interface LogoConcept {
  id: string;
  /** Short internal name, e.g. "The Link". */
  name: string;
  name_es: string;
  /** What the symbol is meant to communicate. */
  idea: string;
  idea_es: string;
  /** Why it survives a favicon / app icon. */
  rationale: string;
  rationale_es: string;
  /** Inner SVG markup on a 64×64 grid. Developer-authored, never user input. */
  art: (inks: Inks) => string;
}

/* ───────────────────────────────────────────────────────────────────────────
   THE FIVE CONCEPTS
   ─────────────────────────────────────────────────────────────────────────── */

export const LOGO_CONCEPTS: LogoConcept[] = [
  /* 1 ─────────────────────────────────────────────────────────── MARKETPLACE LINK */
  {
    id: 'link',
    name: 'The Link',
    name_es: 'El Enlace',
    idea: 'Two open half-rings interlock — the person with the work and the crew who does it, joined by the platform.',
    idea_es: 'Dos medios anillos abiertos se entrelazan: quien tiene el trabajo y la cuadrilla que lo hace, unidos por la plataforma.',
    rationale:
      'A chain link is the single clearest visual metaphor for a two-sided marketplace, and it is pure geometry — no industry clipart. The interlock still reads as one connected object at 16px, and the orange half instantly signals which side is the contractor.',
    rationale_es:
      'Un eslabón es la metáfora más clara de un marketplace de dos lados, y es geometría pura, sin imágenes de la industria. El entrelazado se sigue leyendo como un solo objeto a 16px.',
    art: ({ primary, neutral }) => `
      <path d="M30 19h-5a13 13 0 0 0 0 26h5" fill="none" stroke="${neutral}"
            stroke-width="7" stroke-linecap="round"/>
      <path d="M34 45h5a13 13 0 0 0 0-26h-5" fill="none" stroke="${primary}"
            stroke-width="7" stroke-linecap="round"/>`,
  },

  /* 2 ─────────────────────────────────────────────────────────── SIGNATURE ICON */
  {
    id: 'gate',
    name: 'The Gate',
    name_es: 'La Puerta',
    idea: 'A single continuous stroke that is simultaneously a G and an open gateway onto the jobsite.',
    idea_es: 'Un solo trazo continuo que es al mismo tiempo una G y una entrada abierta a la obra.',
    rationale:
      'This is the ownable option: a custom letterform nobody else has, so it can eventually stand alone the way the Airbnb bélo does. One unbroken stroke means there are no small counters to fill in and blur, and the deliberate opening on the right keeps it from reading as a plain circle at small sizes.',
    rationale_es:
      'Es la opción más propia: una letra hecha a medida que nadie más tiene, capaz de funcionar sin el nombre. Un solo trazo continuo evita huecos pequeños que se difuminan al reducir.',
    art: ({ primary, neutral }) => `
      <path d="M45 22a15 15 0 1 0 2 20v-8H34" fill="none" stroke="${primary}"
            stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="47" cy="17" r="4" fill="${neutral}"/>`,
  },

  /* 3 ─────────────────────────────────────────────────────────── GROWTH ARROW */
  {
    id: 'climb',
    name: 'The Climb',
    name_es: 'El Ascenso',
    idea: 'A route stepping up and to the right, breaking past the baseline — more work, won faster.',
    idea_es: 'Una ruta que sube hacia la derecha y rompe la línea base: más trabajo, ganado más rápido.',
    rationale:
      'Reads as momentum in about a tenth of a second, which is what a performance-led marketplace wants above the fold. The kink in the path keeps it from looking like a generic stock-chart arrow, and the baseline anchors it so the mark is not floating inside the tile.',
    rationale_es:
      'Comunica impulso de inmediato, que es lo que busca un marketplace de rendimiento. El quiebre del trazo evita que parezca una flecha de bolsa genérica.',
    art: ({ primary, neutral }) => `
      <path d="M17 41 30 28l8 8 11-13" fill="none" stroke="${primary}"
            stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M38 21h12v12" fill="none" stroke="${primary}"
            stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M16 50h32" fill="none" stroke="${neutral}" stroke-width="6"
            stroke-linecap="round" opacity="0.55"/>`,
  },

  /* 4 ─────────────────────────────────────────────────────────── TRADE NETWORK */
  {
    id: 'triad',
    name: 'The Triad',
    name_es: 'La Tríada',
    idea: 'Three connected nodes: homeowner, general contractor, subcontractor — the three sides this platform actually joins.',
    idea_es: 'Tres nodos conectados: dueño de casa, contratista general y subcontratista, los tres lados que une la plataforma.',
    rationale:
      'The only concept that encodes the real business model rather than a feeling, and dots-plus-lines is the most favicon-proof shape there is — circles never break down when scaled. The orange apex gives the mark a clear top so it never looks rotated.',
    rationale_es:
      'Es el único concepto que representa el modelo de negocio real, y los puntos con líneas son la forma más resistente al reducir a tamaño de favicon.',
    art: ({ primary, neutral }) => `
      <path d="M32 21 18 44h28z" fill="none" stroke="${neutral}" stroke-width="5"
            stroke-linejoin="round" opacity="0.5"/>
      <circle cx="32" cy="20" r="7" fill="${primary}"/>
      <circle cx="18" cy="45" r="6" fill="${neutral}"/>
      <circle cx="46" cy="45" r="6" fill="${neutral}"/>`,
  },

  /* 5 ─────────────────────────────────────────────────────────── INSTANT MATCH */
  {
    id: 'bolt',
    name: 'The Fast Forward',
    name_es: 'El Avance',
    idea: 'Two stacked chevrons — the gig is posted, unlocked and gone.',
    idea_es: 'Dos galones apilados: el trabajo se publica, se desbloquea y se va.',
    rationale:
      'The literal expression of the name: a fast-forward. It is the simplest mark of the five, which makes it the most reliable at 16px and the best fit for a future animated loading state, but it is also the least specific to construction.',
    rationale_es:
      'La expresión literal del nombre: avance rápido. Es la marca más simple de las cinco y la más fiable a 16px, aunque la menos específica del sector.',
    art: ({ primary, neutral }) => `
      <path d="M21 20l12 12-12 12" fill="none" stroke="${neutral}" stroke-width="7"
            stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M37 20l12 12-12 12" fill="none" stroke="${primary}" stroke-width="7"
            stroke-linecap="round" stroke-linejoin="round"/>`,
  },
];

/**
 * ACTIVE CONCEPT.
 *
 * "The Link" ships as the default: of the three concepts flagged as priorities
 * it is the one that communicates a two-sided marketplace without any words,
 * which is the job the mark has to do in a 16px browser tab.
 *
 * Change this one line to switch the mark everywhere — header, footer, brand
 * studio and the favicon export all read from it.
 */
export const DEFAULT_CONCEPT_ID = 'link';

/** Look up a concept, falling back to the default rather than throwing. */
export const getConcept = (id?: string | null): LogoConcept =>
  LOGO_CONCEPTS.find((c) => c.id === id) ||
  LOGO_CONCEPTS.find((c) => c.id === DEFAULT_CONCEPT_ID) ||
  LOGO_CONCEPTS[0];

/* ───────────────────────────────────────────────────────────────────────────
   RENDERING HELPERS
   ─────────────────────────────────────────────────────────────────────────── */

/** Inks for a given variant. `currentColor` lets the glyph inherit text colour. */
export const inksFor = (variant: LogoVariant): Inks =>
  variant === 'tile'
    ? { primary: LOGO_INK.orange, neutral: LOGO_INK.white }
    : { primary: LOGO_INK.orange, neutral: 'currentColor' };

/** Inner markup for a concept: the navy squircle plus the artwork. */
export const conceptInner = (concept: LogoConcept, variant: LogoVariant): string => {
  const art = concept.art(inksFor(variant));
  return variant === 'tile'
    ? `<rect width="64" height="64" rx="15" fill="${LOGO_INK.navy}"/>${art}`
    : art;
};

/**
 * A complete, standalone SVG document string. Used for the favicon data URL,
 * the "download asset" button and anywhere an <img src> is needed. Neutral ink
 * is resolved to a real colour because `currentColor` has nothing to inherit
 * from outside the DOM.
 */
export const conceptSvgDocument = (
  concept: LogoConcept,
  variant: LogoVariant = 'tile',
  neutralOverride?: string,
): string => {
  const inks =
    variant === 'tile'
      ? inksFor('tile')
      : { primary: LOGO_INK.orange, neutral: neutralOverride || LOGO_INK.navy };
  const body =
    variant === 'tile'
      ? `<rect width="64" height="64" rx="15" fill="${LOGO_INK.navy}"/>${concept.art(inks)}`
      : concept.art(inks);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="GIGGONE">${body.replace(/\s+/g, ' ').trim()}</svg>`;
};

/** `data:` URL form, ready to paste into a <link rel="icon"> href. */
export const conceptDataUrl = (concept: LogoConcept, variant: LogoVariant = 'tile'): string =>
  `data:image/svg+xml,${encodeURIComponent(conceptSvgDocument(concept, variant))}`;
